/**
 * AWARE Hair - Stylist Trial Kit Landing Page JS
 * File: trial-kit-lp.js
 * Loaded deferred. Handles: ViewContent on load, form validation,
 * webhook POST, Meta Lead + TrialKitRequested dedup, inline success,
 * deposit CTA reveal.
 *
 * Dedup pattern (05-implementation-spec.md §3):
 *   event_id generated server-side in N8N, returned in webhook response,
 *   then passed to fbq() so browser + CAPI share the same ID.
 */

(function () {
  'use strict';

  /* ── Helpers ─────────────────────────────────────────── */

  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  function getUtmParam(key) {
    return new URLSearchParams(window.location.search).get(key) || '';
  }

  /* ── Meta click-id (_fbc) reconstruction ─────────────────────
     On Shopify no base Meta pixel runs in the theme DOM, so the _fbc
     cookie is never written from an ad click's fbclid. We rebuild it to
     Meta spec (fb.1.<ts_ms>.<fbclid>) so the server-side CAPI Lead carries
     the click id for attribution. A fresh fbclid in the URL always wins
     (matches real-pixel behaviour); otherwise fall back to any stored _fbc.
     Fails closed to '' and never throws, so it cannot block form init.
     _fbp is passthrough only - we do not fabricate one (no browser pixel to
     match it against, and it would set a cookie for every organic visitor). */
  function setFbCookie(name, value) {
    try {
      document.cookie = name + '=' + encodeURIComponent(value) +
        ';path=/;max-age=' + (90 * 24 * 60 * 60) + ';SameSite=Lax;Secure';
    } catch (e) { /* cookie write blocked (sandboxed context) - non-fatal */ }
  }

  function resolveFbc() {
    try {
      const fbclid = getUtmParam('fbclid');
      if (fbclid) {
        const existing = getCookie('_fbc');
        /* reuse only when the stored cookie already encodes this same click */
        if (existing && existing.split('.').slice(3).join('.') === fbclid) return existing;
        const fbc = 'fb.1.' + Date.now() + '.' + fbclid;
        setFbCookie('_fbc', fbc);
        return fbc;
      }
      return getCookie('_fbc') || '';
    } catch (e) {
      return '';
    }
  }

  function showError(input, msgEl, message) {
    input.classList.add('is-error');
    if (msgEl) {
      msgEl.textContent = message;
      msgEl.classList.add('is-visible');
    }
  }

  function clearError(input, msgEl) {
    input.classList.remove('is-error');
    if (msgEl) msgEl.classList.remove('is-visible');
  }

  /* ── ViewContent on page load ────────────────────────── */

  function fireViewContent() {
    if (typeof fbq !== 'function') return;
    fbq('track', 'ViewContent', {
      content_name: 'stylist-trial',
      content_category: 'stylist-trial'
    });
  }

  /* ── Form init ────────────────────────────────────────── */

  function initForm() {
    const form = document.getElementById('trialKitForm');
    if (!form) return;

    const webhookUrl = form.dataset.webhookUrl || '';
    const depositUrl = form.dataset.depositUrl || '';

    /* Live inline validation on blur */
    const requiredFields = form.querySelectorAll('[data-required]');
    requiredFields.forEach(function (field) {
      field.addEventListener('blur', function () {
        validateField(field);
      });
      field.addEventListener('input', function () {
        if (field.classList.contains('is-error')) validateField(field);
      });
    });

    form.addEventListener('submit', handleSubmit);

    function validateField(field) {
      const msgEl = document.getElementById(field.id + '_error');
      const value = field.value.trim();
      const type = field.dataset.validate || '';

      if (!value) {
        showError(field, msgEl, 'This field is required.');
        return false;
      }

      if (type === 'email') {
        const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        if (!ok) {
          showError(field, msgEl, 'Enter a valid email address.');
          return false;
        }
      }

      if (type === 'license') {
        const ok = /^[A-Za-z0-9]{5,20}$/.test(value);
        if (!ok) {
          showError(field, msgEl, 'License number must be 5-20 alphanumeric characters.');
          return false;
        }
      }

      if (type === 'zip') {
        const ok = /^\d{5}$/.test(value);
        if (!ok) {
          showError(field, msgEl, 'Enter a 5-digit ZIP code.');
          return false;
        }
      }

      if (type === 'phone') {
        /* Forgiving: ignore spaces, dashes, parens, dots, leading +.
           Accept a 10-digit US number, or 11 digits when it starts with 1. */
        const digits = value.replace(/\D/g, '');
        const ok = digits.length === 10 || (digits.length === 11 && digits.charAt(0) === '1');
        if (!ok) {
          showError(field, msgEl, 'Enter a valid phone number.');
          return false;
        }
      }

      clearError(field, msgEl);
      return true;
    }

    function validateAll() {
      let valid = true;
      requiredFields.forEach(function (field) {
        if (!validateField(field)) valid = false;
      });
      return valid;
    }

    /* In-flight guard: prevent duplicate submits */
    var isSubmitting = false;

    async function handleSubmit(e) {
      e.preventDefault();

      /* In-flight guard */
      if (isSubmitting) return;

      /* Webhook URL sanity check before touching the DOM */
      if (!webhookUrl || !webhookUrl.startsWith('https://')) {
        var cfgErr = form.querySelector('.tklp__form-config-error');
        if (!cfgErr) {
          cfgErr = document.createElement('p');
          cfgErr.className = 'tklp__error-msg tklp__form-config-error is-visible';
          cfgErr.style.cssText = 'display:block;margin-bottom:12px;font-size:0.875rem;';
          form.insertBefore(cfgErr, form.firstChild);
        }
        cfgErr.textContent = 'Configuration error: form cannot be submitted right now. Please try refreshing the page.';
        cfgErr.classList.add('is-visible');
        return;
      }

      if (!validateAll()) {
        /* Scroll to first error */
        const firstErr = form.querySelector('.is-error');
        if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const submitBtn = document.getElementById('trialKitSubmit');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Submitting...';
      }

      isSubmitting = true;

      /* Build payload */
      const payload = {
        first_name:       form.querySelector('#tk_first_name').value.trim(),
        last_name:        form.querySelector('#tk_last_name').value.trim(),
        email:            form.querySelector('#tk_email').value.trim(),
        phone:            form.querySelector('#tk_phone').value.trim(),
        license_number:   form.querySelector('#tk_license_number').value.trim(),
        license_state:    form.querySelector('#tk_license_state').value,
        salon_name:       form.querySelector('#tk_salon_name').value.trim(),
        salon_role:       form.querySelector('#tk_salon_role').value,
        salon_zip:        form.querySelector('#tk_salon_zip').value.trim(),
        how_did_you_hear: form.querySelector('#tk_hear_about').value,
        num_stylists:     form.querySelector('#tk_num_stylists') ? form.querySelector('#tk_num_stylists').value : '',
        notes:            form.querySelector('#tk_notes') ? form.querySelector('#tk_notes').value.trim() : '',
        shipping_address: form.querySelector('#tk_ship_address') ? form.querySelector('#tk_ship_address').value.trim() : '',
        shipping_city:    form.querySelector('#tk_ship_city') ? form.querySelector('#tk_ship_city').value.trim() : '',
        shipping_state:   form.querySelector('#tk_ship_state') ? form.querySelector('#tk_ship_state').value : '',
        /* Email marketing consent is now required to request a kit (opt-in checkbox
           removed); consent is captured by the disclosure shown above the submit button. */
        scalp_report_optin:    true,
        email_marketing_optin: true,
        /* Attribution */
        utm_source:       getUtmParam('utm_source'),
        utm_medium:       getUtmParam('utm_medium'),
        utm_campaign:     getUtmParam('utm_campaign'),
        utm_content:      getUtmParam('utm_content'),
        utm_term:         getUtmParam('utm_term'),
        /* Meta match keys (reconstruct _fbc from fbclid; fbp passthrough only) */
        fbc:              resolveFbc(),
        fbp:              getCookie('_fbp'),
        user_agent:       navigator.userAgent,
        page_url:         window.location.href
      };

      /* Helper: show a retryable submission error */
      function showSubmitError(msg) {
        var errEl = form.querySelector('.tklp__form-submit-error');
        if (!errEl) {
          errEl = document.createElement('p');
          errEl.className = 'tklp__error-msg tklp__form-submit-error is-visible';
          errEl.style.cssText = 'display:block;margin-top:12px;font-size:0.875rem;';
          var btn = document.getElementById('trialKitSubmit');
          if (btn && btn.parentNode) {
            btn.parentNode.insertBefore(errEl, btn.nextSibling);
          } else {
            form.appendChild(errEl);
          }
        }
        errEl.textContent = msg;
        errEl.classList.add('is-visible');
      }

      let eventId = null;
      let submitOk = false;

      try {
        /* AbortController with 15s timeout */
        const controller = new AbortController();
        const timeoutId = setTimeout(function () { controller.abort(); }, 15000);

        // text/plain so n8n receives the raw JSON string in $json.body
        // (matches the proven scalp-report pattern; the trial-submit Function
        // node does JSON.parse($json.body), which needs a string, not an object).
        const response = await fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain' },
          body: JSON.stringify(payload),
          signal: controller.signal
        });

        if (!response.ok) {
          clearTimeout(timeoutId);
          throw new Error('Server error: ' + response.status);
        }

        let data = {};
        try { data = await response.json(); } catch (_) {}
        clearTimeout(timeoutId);   // cleared after the body read so a stalled body still times out

        /* Retrieve server-generated event_id for dedup */
        eventId = data.event_id || null;
        submitOk = true;

      } catch (err) {
        console.error('[AWARE Trial] Webhook error:', err);
        var errMsg = err && err.name === 'AbortError'
          ? 'The request timed out. Please check your connection and try again.'
          : 'Something went wrong. Please try again in a moment.';
        showSubmitError(errMsg);
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Request your free kit';
        }
      } finally {
        isSubmitting = false;
      }

      /* Bail out if the submission failed */
      if (!submitOk) return;

      /* ── Fire Meta browser events via the Shopify Customer Events sandbox ──
         fbq does NOT exist in the theme window on Shopify (it lives in the
         pixel sandbox). We publish a `custom_event` that the AWARE Custom Pixel
         (Settings > Customer Events) subscribes to and forwards to fbq with the
         dedup eventID, so browser + CAPI dedupe on the shared event_id.
         Only fire when event_id is present (else CAPI already counted it). */
      if (window.Shopify && window.Shopify.analytics && eventId) {
        try {
          /* NO plaintext PII here: custom-data is not hashed; the server CAPI
             already sends hashed em/fn/ln/zp/st under these same event ids. */
          window.Shopify.analytics.publish('custom_event', {
            event_name:   'Lead',
            event_id:     eventId,
            content_name: 'stylist-trial',
            fbc:          payload.fbc,
            fbp:          payload.fbp
          });
          /* TrialKitRequested must use the CAPI-derived id (drops the leading
             "Lead" segment) so it dedupes against the server TrialKitRequested. */
          window.Shopify.analytics.publish('custom_event', {
            event_name:    'TrialKitRequested',
            event_id:      'TrialKitRequested_' + eventId.split('_').slice(1).join('_'),
            content_name:  'stylist-trial',
            license_state: payload.license_state,
            salon_role:    payload.salon_role,
            salon_zip:     payload.salon_zip
          });
        } catch (pixelErr) {
          /* A pixel failure must never block the success state shown below. */
          console.error('[AWARE Trial] pixel publish error:', pixelErr);
        }
      }

      /* ── Reveal inline success state ─────────────────── */
      form.style.display = 'none';

      const successEl = document.getElementById('trialKitSuccess');
      if (successEl) {
        successEl.classList.add('is-visible');
        successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      /* ── Reveal deposit CTA ──────────────────────────── */
      if (depositUrl) {
        const depositEl = document.getElementById('trialKitDepositCta');
        const depositLink = document.getElementById('trialKitDepositLink');
        if (depositEl && depositLink) {
          depositLink.href = depositUrl;
          depositEl.classList.add('is-visible');
        }
      }
    }
  }

  /* ── FAQ accordion (native <details>) ──────────────── */
  /* Native <details>/<summary> handles open/close; we only
     need the icon rotation class on the parent. */
  function initFaq() {
    const items = document.querySelectorAll('.tklp__faq-item');
    items.forEach(function (item) {
      item.addEventListener('toggle', function () {
        /* handled by CSS [open] selector on .tklp__faq-icon */
      });
    });
  }

  /* ── Boot ────────────────────────────────────────────── */
  function boot() {
    resolveFbc();   /* persist ad click-id (_fbc) from fbclid on landing */
    fireViewContent();
    initForm();
    initFaq();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

}());
